import { typographyToCss } from 'shared/lib/mui';
import styled, { css } from 'styled-components';

export const Container = styled.span`
  display: flex;
  align-items: center;
`;

export const Label = styled.label<{ $disabled: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.45;
    `};
`;

export const InputContainer = styled.span`
  position: relative;
  display: inline-block;
  width: 100%;
`;

export const Input = styled.input<{
  $hasError: boolean;
  $hasLeft: boolean;
  $hasRight: boolean;
  $transparent: boolean;
  $isSmall: boolean;
  $hasNumberButtons: boolean;
  $postFixWidth: number;
}>`
  ${({ theme, $isSmall }) =>
    typographyToCss(
      $isSmall ? theme.typography.button : theme.typography.body2,
    )};

  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  width: 100%;
  field-sizing: content;

  background: ${({ theme, $transparent }) =>
    $transparent ? 'transparent' : theme.palette.background.default};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  border: 1px solid ${({ theme }) => theme.palette.grey[400]};
  transition: border-color 200ms;

  color: inherit;

  &::-webkit-input-placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  &::-moz-placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  &:-ms-input-placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  &[type='number'] {
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    appearance: textfield;

    text-align: right;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
    }
  }

  &[inputMode='numeric'] {
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    appearance: textfield;

    text-align: right;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
    }
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.primary.main};
  }

  &:disabled {
    opacity: 0.45;
  }

  ${({ theme, $hasNumberButtons, $isSmall }) =>
    $hasNumberButtons &&
    css`
      padding-right: ${theme.spacing($isSmall ? 5 : 7)};
    `};

  ${({ theme, $hasError }) =>
    $hasError &&
    css`
      border-color: ${theme.palette.error.main};
    `};

  ${({ theme, $isSmall }) =>
    $isSmall &&
    css`
      padding: ${`${theme.spacing(0.75)} ${theme.spacing(1)}`};
    `};

  ${({ theme, $isSmall }) =>
    theme.isMobile &&
    css`
      padding: ${`${theme.spacing($isSmall ? 2 : 2.5)} ${theme.spacing($isSmall ? 2 : 3)}`};
    `};

  ${({ $hasLeft, $isSmall, theme }) =>
    $hasLeft &&
    css`
      padding-left: ${theme.spacing($isSmall ? 5 : 7)};
    `};

  ${({ $hasRight, $isSmall, theme, $postFixWidth }) =>
    $hasRight &&
    css`
      padding-right: ${`calc(${$postFixWidth ?? 0}px + ${theme.spacing($isSmall ? 5 : 7)})`};
    `};
`;

export const StartContainer = styled.span<{ $isSmall: boolean }>`
  position: absolute;
  top: 50%;
  height: 100%;
  left: ${({ theme, $isSmall }) => theme.spacing($isSmall ? 1.5 : 2)};

  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(0.5)};
  align-items: center;

  transform: translateY(-50%);
`;
export const EndContainer = styled.span<{ $isSmall: boolean }>`
  position: absolute;
  top: 50%;
  height: 100%;
  right: ${({ theme, $isSmall }) => theme.spacing($isSmall ? 1.5 : 2)};

  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(0.5)};
  align-items: center;

  transform: translateY(-50%);
`;

export const PostFix = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};

  pointer-events: none;

  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const AffixIconContainer = styled.span<{ $color: string | undefined }>`
  pointer-events: none;

  line-height: 62.5%;

  ${({ $color }) =>
    $color &&
    css`
      color: ${$color};
    `};
`;

export const NumberButtonsContainer = styled.span`
  display: flex;
  flex-direction: column;

  padding: ${({ theme }) => theme.spacing(0.5)};
`;

const ButtonBase = css<{ $isSmall: boolean }>`
  margin: 0;
  padding: 0;

  background: transparent;
  border: none;
  cursor: pointer;

  font-size: ${({ $isSmall }) => ($isSmall ? '1rem' : '1.2rem')};
  line-height: 62.5%;
`;

export const NumberButton = styled.button<{
  $isSecond?: boolean;
  $isSmall: boolean;
}>`
  ${ButtonBase};

  display: flex;
  align-items: center;
  justify-content: center;

  transform: rotate(${({ $isSecond }) => ($isSecond ? 270 : 90)}deg);

  color: inherit;
`;

export const ClearButton = styled.button<{ $isSmall: boolean }>`
  ${ButtonBase};

  margin: 0;
  padding: 0;

  border: none;
  background: transparent;

  color: ${({ theme }) => theme.palette.text.secondary};
`;
